import { commissionCopy } from "../../../supabase/functions/_shared/clinicianSettlementPolicy.mjs";
// src/pages/legal/legalContent.js

export const LEGAL_TERMS_VERSION = "2026-09-21";
export const LEGAL_PRIVACY_VERSION = "2026-03-05";

const LEGAL_SECTION_DEFINITIONS = {
  privacy: [
    {
      id: "whoWeAre",
      title: "Who We Are",
      body: [
        "AfriMed™ Telehealth provides digital healthcare services for patients and healthcare professionals, including teleconsultation workflows, appointment scheduling, secure messaging, profile onboarding, and verification-enabled healthcare operations.",
        "This Privacy Policy applies to personal data collected through AfriMed™ web and app experiences, including staging and production app domains.",
      ],
    },
    {
      id: "informationWeCollect",
      title: "Information We Collect",
      body: [
        "We collect account data (name, email, phone, role), profile and onboarding data (for example, demographics and medical context you provide), service activity data (appointments, messages, settings), and technical data (device/browser metadata, logs, and security signals).",
        "Where you upload healthcare or verification documents, we process those files and related metadata for account verification, compliance workflows, and service delivery.",
      ],
    },
    {
      id: "howWeUseYourInformation",
      title: "How We Use Your Information",
      body: [
        "We use personal data to create and secure accounts, authenticate users, deliver telehealth features, support appointments and communications, improve reliability, detect abuse, and comply with healthcare and legal obligations.",
        "We may use operational analytics to improve service quality and user experience while applying minimization and access control practices.",
      ],
    },
    {
      id: "howWeProtectYourInformation",
      title: "How We Protect Your Information",
      body: [
        "AfriMed™ applies layered technical and organizational safeguards, including access controls, encryption in transit, role-based authorization, monitoring, and audit-oriented workflows.",
        "No online system is fully risk-free; we continuously improve controls to reduce security and privacy risk.",
      ],
    },
    {
      id: "howWeShareInformation",
      title: "How We Share Information",
      body: [
        "We share data only when needed for service delivery, legal compliance, user-directed workflows, or trusted subprocessors operating under confidentiality and data protection obligations.",
        "We do not sell personal information.",
      ],
    },
    {
      id: "dataRetention",
      title: "Data Retention",
      body: [
        "We retain personal data for as long as needed to deliver services, meet legal and regulatory obligations, resolve disputes, enforce agreements, and maintain audit records.",
        "Retention periods may vary by data type and jurisdictional requirements.",
      ],
    },
    {
      id: "cookiesAndSimilarTechnologies",
      title: "Cookies and Similar Technologies",
      body: [
        "We use essential cookies and similar technologies for security, session continuity, and product functionality.",
        "Where applicable, optional categories are managed through the cookie preferences controls available in the app experience.",
      ],
    },
    {
      id: "yourDataRights",
      title: "Your Data Rights",
      body: [
        "Depending on your jurisdiction, you may have rights to access, correct, delete, restrict, object to, or port certain personal data.",
        "You may also request account and privacy support through our contact channels listed below.",
      ],
    },
    {
      id: "childrensPrivacy",
      title: "Children's Privacy",
      body: [
        "AfriMed™ is not intended for unsupervised use by children where prohibited by law.",
        "If you believe data was provided inappropriately, contact us and we will review and act in line with legal obligations.",
      ],
    },
    {
      id: "internationalDataUse",
      title: "International Data Use",
      body: [
        "AfriMed™ may process data across regions as needed for secure service operation.",
        "Where cross-border transfer is required, we apply appropriate safeguards consistent with applicable law.",
      ],
    },
    {
      id: "changesToThisPolicy",
      title: "Changes to This Policy",
      body: [
        "We may update this Privacy Policy as the platform, regulations, or security requirements evolve.",
        "Material updates will be reflected by revising the effective date and publishing the updated version on this page.",
      ],
    },
    {
      id: "contactUs",
      title: "Contact Us",
      body: [
        "AfriMed™ Telehealth",
        "Email: info@afrimedtelehealth.com",
        "Website: https://afrimedtelehealth.com",
      ],
    },
  ],
  terms: [
    {
      id: "acceptanceOfTerms",
      title: "Acceptance of Terms",
      body: [
        "By accessing or using AfriMed™ services, you agree to these Terms and Conditions and any applicable policies referenced here.",
        "If you do not agree, do not use the service.",
      ],
    },
    {
      id: "eligibilityAndAccountRegistration",
      title: "Eligibility and Account Registration",
      body: [
        "You must provide accurate information when registering and keep your account credentials secure.",
        "You are responsible for activity that occurs under your account unless caused by our security failure.",
      ],
    },
    {
      id: "serviceScope",
      title: "Service Scope",
      body: [
        "AfriMed™ provides digital healthcare infrastructure, including telehealth communication and care coordination workflows.",
        "AfriMed™ does not replace emergency services. In urgent or life-threatening situations, contact local emergency services immediately.",
      ],
    },
    {
      id: "appointmentsBookingsAndCancellations",
      title: "Appointments, Bookings, and Cancellations",
      body: [
        "Appointment availability, scheduling, changes, and cancellation windows may depend on practitioner, institution, and operational policies.",
        "Users must provide correct booking information and act in good faith when rescheduling or cancelling.",
      ],
    },
    {
      id: "paymentsAndBilling",
      title: "Payments and Billing",
      body: [
        commissionCopy.en,
        "AfriMed™ acts as a platform and facilitator. Commission applies from the first qualifying paid consultation. Where supported, funds may be authorized before service; capture is governed by authoritative service completion. Cancellation before capture may release the authorization. Refunds after capture follow the applicable provider and reversal process.",
        "The healthcare provider earns the service amount subject to commission and provider processing fees. Bank credit follows external provider and banking settlement timelines; immediate settlement is not promised. AfriMed™ does not operate an internal clinician wallet or escrow.",
      ],
    },
    {
      id: "userResponsibilitiesAndAcceptableUse",
      title: "User Responsibilities and Acceptable Use",
      body: [
        "You agree not to misuse the platform, interfere with security controls, submit fraudulent information, or use AfriMed™ for unlawful activity.",
        "Healthcare users must uphold applicable professional and confidentiality standards.",
      ],
    },
    {
      id: "intellectualProperty",
      title: "Intellectual Property",
      body: [
        "AfriMed™ trademarks, product design, content, and platform software are protected by applicable intellectual property laws.",
        "You may not copy, reverse engineer, or redistribute protected materials except as permitted by law or written authorization.",
      ],
    },
    {
      id: "thirdPartyServices",
      title: "Third-Party Services",
      body: [
        "Certain flows may rely on trusted third-party services, for example authentication, infrastructure, communications, or map providers.",
        "Their services may be governed by their own terms and privacy notices.",
      ],
    },
    {
      id: "privacyAndDataHandling",
      title: "Privacy and Data Handling",
      body: [
        "Use of personal data is governed by the AfriMed™ Privacy Policy.",
        "By using the platform, you acknowledge data processing necessary for authentication, healthcare service delivery, and compliance workflows.",
      ],
    },
    {
      id: "disclaimersAndLimitationOfLiability",
      title: "Disclaimers and Limitation of Liability",
      body: [
        "AfriMed™ is provided on an 'as available' basis, subject to maintenance, operational limits, and external dependencies.",
        "To the extent permitted by law, liability is limited for indirect, incidental, or consequential damages arising from use of the platform.",
      ],
    },
    {
      id: "suspensionAndTermination",
      title: "Suspension and Termination",
      body: [
        "We may suspend or terminate access where required for security, legal compliance, policy violations, or abuse prevention.",
        "You may stop using the service at any time, subject to applicable legal and contractual obligations.",
      ],
    },
    {
      id: "governingLawAndUpdates",
      title: "Governing Law and Updates",
      body: [
        "These terms are governed by applicable laws and regulations relevant to AfriMed™ operations and the jurisdictions in which services are offered.",
        "We may revise these terms; the latest version and effective date are published on this page.",
      ],
    },
    {
      id: "contact",
      title: "Contact",
      body: [
        "AfriMed™ Telehealth",
        "Email: info@afrimedtelehealth.com",
        "Website: https://afrimedtelehealth.com",
      ],
    },
  ],
  dataDeletion: [
    {
      id: "purposeAndScope",
      title: "Purpose and Scope",
      body: [
        "This page explains how AfriMed™ users can request deletion of platform data linked to their account.",
        "Platform data in this context includes account and profile records, service usage records, and app-generated metadata that AfriMed™ controls in its systems.",
      ],
    },
    {
      id: "howToRequestDeletion",
      title: "How to Request Deletion",
      body: [
        "Users can request deletion by emailing info@afrimedtelehealth.com from the email address linked to their AfriMed™ account.",
        "If email is not available, users may submit the request through AfriMed™ support channels and include identifying details for verification.",
        "For faster processing, include your full name, account email or phone number, role (patient or doctor), and the statement: 'I request deletion of my AfriMed™ account data.'",
      ],
    },
    {
      id: "identityVerificationBeforeDeletion",
      title: "Identity Verification Before Deletion",
      body: [
        "For safety and healthcare privacy protection, AfriMed™ verifies the requester's identity before processing deletion.",
        "Verification may include confirmation from the registered email or phone number and additional checks where needed to prevent unauthorized account deletion.",
      ],
    },
    {
      id: "whatWeDelete",
      title: "What We Delete",
      body: [
        "After successful verification, AfriMed™ deletes or irreversibly de-identifies account-linked platform data that is no longer required for legal, regulatory, security, or fraud-prevention obligations.",
        "Examples may include profile details, non-required media files, and application-level activity records associated with the account.",
      ],
    },
    {
      id: "whatMayBeRetained",
      title: "What May Be Retained",
      body: [
        "For deletion-request workflows, AfriMed™ may retain limited records for up to 90 days to complete verification, dispute handling, security checks, and operational safeguards.",
        "After that window, deletion is applied automatically unless a specific legal, regulatory, fraud-prevention, or audit hold requires longer retention.",
        "Any retained data is access-restricted and stored only for the required retention period.",
      ],
    },
    {
      id: "processingTimeline",
      title: "Processing Timeline",
      body: [
        "AfriMed™ acknowledges deletion requests and begins verification and processing as soon as reasonably possible.",
        "Completion timelines depend on identity verification and legal obligations, but requests are typically completed within 30 days.",
      ],
    },
    {
      id: "outcomeConfirmation",
      title: "Outcome Confirmation",
      body: [
        "Once processing is completed, AfriMed™ sends a confirmation response to the requester.",
        "If any data cannot be deleted due to legal obligations, AfriMed™ explains the reason and the applicable retention basis.",
      ],
    },
    {
      id: "contactForDataDeletion",
      title: "Contact for Data Deletion",
      body: [
        "AfriMed™ Telehealth",
        "Email: info@afrimedtelehealth.com",
        "Reference URL: https://staging.afrimed.tech/legal/data-deletion",
      ],
    },
  ],
};

function buildLegalSections(t, documentKey, definitions = []) {
  return definitions.map((section) => ({
    title: t(
      `legal.${documentKey}.sections.${section.id}.title`,
      section.title,
    ),
    body: section.body.map((paragraph, index) =>
      t(
        `legal.${documentKey}.sections.${section.id}.body.${index + 1}`,
        paragraph,
      ),
    ),
  }));
}

export function getLegalEffectiveDate(t) {
  return t("legal.common.effectiveDate", "March 5, 2026");
}

export function getPrivacySections(t) {
  return buildLegalSections(t, "privacy", LEGAL_SECTION_DEFINITIONS.privacy);
}

export function getTermsSections(t) {
  return buildLegalSections(t, "terms", LEGAL_SECTION_DEFINITIONS.terms);
}

export function getDataDeletionSections(t) {
  return buildLegalSections(t, "dataDeletion", LEGAL_SECTION_DEFINITIONS.dataDeletion);
}
